import { useRef, useState, useEffect } from 'react';
import './GenerationControls.css';
import { RedButton } from './common';
import SupportEmail from './SupportEmail';
import LegalNotice from './LegalNotice';
import { usePhotoGenerate } from '@genfun/core/hooks/usePhotoGenerate';
import { scrollNear, scrollNearAfterRender } from '../utils/autoScroll';

import formSchema from '@genfun/core/data/formSchema.json';
import formSchemaVideo from '@genfun/core/data/formSchemaVideo.json';

function getSchema(type) {
    return type === "video" ? formSchemaVideo : formSchema;
}

// Choice chip component
function ChoiceChip({ text, selected, onClick }) {
    return (
        <div
            className={selected ? "choiceChipSelected" : "choiceChip"}
            onClick={onClick}
        >
            {text}
        </div>
    );
}

// Control input row with header and chips
function ControlInputRow({ header, options, formRef, rowIndex, stages, currentStage, showHeader = true, stageRefs, getGenerateEl }) {
    const [, forceUpdate] = useState(0);
    const selectedIdx = formRef.current[stages.current[rowIndex].name];

    const handleSelect = (optionIndex, option) => {
        // Clear subsequent stage selections
        if (stages.current.length > rowIndex + 1) {
            for (let i = rowIndex + 1; i < stages.current.length; i++) {
                const stageName = stages.current[i].name;
                if (formRef.current.hasOwnProperty(stageName)) {
                    delete formRef.current[stageName];
                }
            }
            stages.current = stages.current.filter((_, idx) => idx <= rowIndex);
        }

        if (option.next !== "generate") {
            // Add next stage and scroll it into view once it renders.
            const schema = getSchema(formRef.current.type || "photo");
            stages.current = [...stages.current, schema[option.next]];
            stages.forceUpdate();
            scrollNearAfterRender(() => stageRefs.current[rowIndex + 1]);
        } else {
            // Last row answered — scroll down to the Generate button.
            scrollNearAfterRender(() => (getGenerateEl ? getGenerateEl() : null));
        }

        // Set selection
        formRef.current[stages.current[rowIndex].name] = optionIndex;
        currentStage.current = option.next;
        currentStage.forceUpdate();
        forceUpdate(prev => prev + 1);
    };

    return (
        <div className="controlCard" ref={el => stageRefs.current[rowIndex] = el}>
            <div className="controlInputRow">
                {showHeader && <div className="controlRowHeader">{header}</div>}
                <div className="controlChips">
                    {options.map((option, index) => (
                        <ChoiceChip
                            key={index}
                            text={option.text}
                            selected={selectedIdx === index}
                            onClick={() => handleSelect(index, option)}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
}

// Control form - maps schema stages to rows
function ControlForm({ currentStage, formRef, initialStages = null, getGenerateEl }) {
    const [upd, setForceUpdate] = useState(0);
    const schema = getSchema(formRef.current.type || "photo");
    const stages = useRef(initialStages || [schema["stage1"]]);
    const prevType = useRef(formRef.current.type);
    const stageRefs = useRef({});

    // Reset stages when type changes
    if (prevType.current !== formRef.current.type) {
        const newSchema = getSchema(formRef.current.type || "photo");
        stages.current = [newSchema["stage1"]];
        prevType.current = formRef.current.type;
        // Clear all stage selections
        Object.keys(formRef.current).forEach(key => {
            if (key.startsWith('stage')) {
                delete formRef.current[key];
            }
        });
    }

    stages.forceUpdate = () => {
        setForceUpdate(prev => prev + 1);
    };

    return (
        <div className="controlFormSection">
            {stages.current.map((stage, index) => (
                <ControlInputRow
                    key={stage.name + '-' + index}
                    header={stage.header}
                    options={stage.options}
                    formRef={formRef}
                    rowIndex={index}
                    stages={stages}
                    currentStage={currentStage}
                    stageRefs={stageRefs}
                    getGenerateEl={getGenerateEl}
                />
            ))}
        </div>
    );
}

// Toggle switch component
function ToggleSwitch({ on, onToggle, label }) {
    return (
        <div className="toggleRow">
            <div
                className={`toggleSwitch ${on ? 'toggleSwitchOn' : ''}`}
                onClick={onToggle}
            >
                <div className="toggleKnob" />
            </div>
            <div className="toggleLabel">{label}</div>
        </div>
    );
}

// Prompt input section
function PromptSection({ promptOn, setPromptOn, promptValue, setPromptValue }) {
    return (
        <div className="controlCard">
            <div className="promptContainer">
                <ToggleSwitch
                    on={promptOn}
                    onToggle={() => setPromptOn(!promptOn)}
                    label="Customize with prompt"
                />
                {promptOn && (
                    <textarea
                        className="promptTextarea"
                        placeholder="Describe what you want to generate..."
                        value={promptValue}
                        onChange={(e) => setPromptValue(e.target.value)}
                    />
                )}
            </div>
        </div>
    );
}

// Photo/Video type selector
function TypeSelector({ formRef, onTypeChange }) {
    const [selectedType, setSelectedType] = useState(formRef.current.type === "video" ? 1 : 0);

    const handleSelect = (index) => {
        setSelectedType(index);
        const newType = index === 0 ? "photo" : "video";
        formRef.current.type = newType;
        if (onTypeChange) onTypeChange(newType);
    };

    return (
        <div className="controlCard">
            <div className="controlInputRow">
                <div className="controlChips">
                    <ChoiceChip
                        text="Photo"
                        selected={selectedType === 0}
                        onClick={() => handleSelect(0)}
                    />
                </div>
            </div>
        </div>
    );
}

// Footer with disclaimers and links
function ControlsFooter() {
    return (
        <div className="controlsFooter">
            <div className="footerDisclaimer">
                All generated content is AI-created. Users must be 18+ and agree to our terms.
            </div>
            <SupportEmail />
            <div className="footerLinks">
                <a href="/terms" className="footerLink">Terms</a>
                <a href="/privacy" className="footerLink">Privacy</a>
                {/*<a href="https://discord.gg/genfun" className="footerLink" target="_blank" rel="noopener noreferrer">Discord</a>*/}
            </div>
            <LegalNotice />
        </div>
    );
}

// Main GenerationControls component
export default function GenerationControls({
    token,
    user,
    setImg,
    setVideo,
    setGenType,
    setGenerated,
    setTaskId,
    setAnimateAction,
    setGenerationConfig,
    setShowPayPopUp,
    setShowVideoPopUp,
    setShowLoginPopup,
    onScrollToResults
}) {
    const formRef = useRef({ type: "photo", number: 1, fastMode: true });
    const currentStage = useRef("stage1");
    const controlsRef = useRef(null);
    const generateButtonRef = useRef(null);
    const [stageUpd, setStageUpd] = useState(0);
    const [promptOn, setPromptOn] = useState(false);
    const [promptValue, setPromptValue] = useState('');
    const errorRef = useRef(null);

    currentStage.forceUpdate = () => {
        setStageUpd(prev => prev + 1);
    };

    const textPromptActive = promptOn && promptValue.trim().length > 0;

    const { handleGenerate, error, setError } = usePhotoGenerate({
        token,
        formRef,
        textPromptActive,
        textPromptValue: promptValue,
        callbacks: {
            setGenType,
            setGenerated,
            setImg,
            setVideo,
            setTaskId,
            setAnimateAction,
            setGenerationConfig,
            setShowVideoPopUp,
            setShowPayPopUp,
            onScrollToResults: onScrollToResults || (() => {}),
            onResetInteraction: () => {}
        }
    });

    // Scroll to error when it appears
    useEffect(() => {
        if (error) scrollNear(errorRef.current, 40);
    }, [error]);

    const handleTypeChange = () => {
        // Reset current stage when type changes
        currentStage.current = "stage1";
        setStageUpd(prev => prev + 1);
    };

    const handleGenerateClick = () => {
        if (!user) {
            if (setShowLoginPopup) setShowLoginPopup(true);
            return;
        }
        handleGenerate();
    };

    // Show generate button when in prompt mode or when form is complete
    const showGenerateButton = promptOn || currentStage.current === "generate";

    return (
        <div className="generationControls" ref={controlsRef}>
            <TypeSelector formRef={formRef} onTypeChange={handleTypeChange} />

            <PromptSection
                promptOn={promptOn}
                setPromptOn={setPromptOn}
                promptValue={promptValue}
                setPromptValue={setPromptValue}
            />

            {!promptOn && (
                <ControlForm
                    currentStage={currentStage}
                    formRef={formRef}
                    getGenerateEl={() => generateButtonRef.current}
                />
            )}

            {showGenerateButton && (
                <div className="generateButtonArea" ref={generateButtonRef}>
                    <RedButton onClick={handleGenerateClick}>
                        Generate
                    </RedButton>
                    {error && <div className="generationError" ref={errorRef}>{error}</div>}
                </div>
            )}

            <ControlsFooter />
        </div>
    );
}
